/**
 * What a `nostr:` URI opens.
 *
 * Pure, and separate from the listener that receives one, because the URI comes
 * from outside the app entirely — another application, a web page, an email — and
 * "which screen does this string open" is the decision an attacker would most like
 * to influence. Every rule here is asserted in `nostrUri.test.ts`.
 *
 * Three of them are load-bearing:
 *
 * 1. **An `nsec` is refused.** A URI is a link somebody sent you; adopting a secret
 *    key from one would let a web page hand a reader an identity and then watch what
 *    they sign with it. There is no route for it and there must not be.
 * 2. **Only the entity decides the destination.** No path, query or fragment is
 *    read, so `nostr:npub1…?open=settings` cannot address anything the entity
 *    itself does not.
 * 3. **Undecodable is a dead end, not a guess.** `decodeAny` returns undefined for
 *    anything malformed and this returns undefined in turn. A deep link that does
 *    nothing is a bug report; one that navigates somewhere unintended is an
 *    incident.
 */

import { COMMUNITY_KIND, communityAddress, decodeAny } from "@setu/protocol";
import type { Route } from "../shell/routes";

/**
 * Where a URI lands.
 *
 * A thread is not a `Route`: the shell holds the open note in state and renders it
 * in the auxiliary column rather than as a destination (see `App.tsx`), so an
 * event reference has to be reported as its own kind of target. Collapsing the two
 * would mean inventing a route the router does not have.
 */
export type NostrUriTarget =
  | { readonly kind: "route"; readonly route: Route }
  | { readonly kind: "thread"; readonly id: string };

/**
 * `nostr:` plus a bech32 entity with relay hints. The Rust side applies the same
 * ceiling; this one is what protects the browser build, where the URI could arrive
 * from a pasted string rather than from the OS.
 */
const MAX_URI_LENGTH = 1024;

/** True for `nostr:…` and `nostr://…`, case-insensitively. */
function hasNostrScheme(raw: string): boolean {
  return /^nostr:(\/\/)?/i.test(raw);
}

/**
 * Resolve a `nostr:` URI to a destination, or `undefined` if it addresses none.
 *
 * The scheme is required. Accepting a bare `npub1…` would make this a general
 * entity opener, and the OS handler is the only caller — a bare entity reaching
 * here means something upstream mangled the URI, which is worth failing on.
 */
export function resolveNostrUri(raw: string): NostrUriTarget | undefined {
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_URI_LENGTH) return undefined;
  if (!hasNostrScheme(trimmed)) return undefined;

  // `decodeAny` strips a single `nostr:` itself; the authority slashes are ours to
  // remove. Anything left over is passed through untouched, so a URI with a path
  // fails to decode rather than being trimmed into one that does.
  const entity = trimmed.replace(/^nostr:(\/\/)?/i, "");
  const ref = decodeAny(entity);
  if (ref === undefined) return undefined;

  switch (ref.type) {
    case "npub":
    case "nprofile":
      return { kind: "route", route: { name: "profile", pubkey: ref.pubkey } };

    case "note":
    case "nevent":
      return { kind: "thread", id: ref.id };

    case "naddr":
      // A community has a screen that takes an address, so it opens as itself.
      if (ref.kind === COMMUNITY_KIND) {
        // Built by the protocol package's own helper, so a deep link and a
        // community parsed from a kind-34550 produce byte-identical addresses —
        // `useCommunity` looks one up by string equality.
        return {
          kind: "route",
          route: {
            name: "community",
            address: communityAddress(ref.pubkey, ref.identifier),
          },
        };
      }
      /*
       * Every other addressable kind — long-form posts, lists, wiki entries —
       * has no screen that addresses one directly, so the closest honest
       * destination is the author. It is a demotion rather than a failure: the
       * reader asked for something Setu holds a place for and lands one step
       * away from it, which beats a link that appears to do nothing. Add a real
       * route here the day an addressable event gets its own screen.
       */
      return { kind: "route", route: { name: "profile", pubkey: ref.pubkey } };

    default:
      /*
       * `nsec` lands here, and that is the point.
       *
       * A URI is attacker-supplied by construction, so a secret key inside one is
       * either a mistake or an attempt to seed an identity the sender controls and
       * can then watch. Neither deserves a route. Falling through the switch also
       * means a NIP-19 prefix added to the protocol package later is inert until
       * somebody decides where it goes.
       */
      return undefined;
  }
}
